import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { STATUS_APPLICATIONS } from '../../constants/statusApplications';
import ApplyTitleHeader from '../common/ApplyTitleHeader';
import ApplyAuth from '../auth/ApplyAuth';
import { sectorImages } from '../../constants/images';
import StatusModal from '../modals/StatusModal';
import { MdOutlineFileDownload } from 'react-icons/md';
import { calculateTimeLeft } from '../../utils/timeUtils';
import { APPLICATION_CLOSE_DATE } from '../../constants/configLoader';
import '../../styles/forms/myApplication.css';
import '../../styles/pages/apply.css';

const MyApplication = ({ application, onEdit, isLoggedIn, userEmail, onLogout }) => {
  const [showDeadlineModal, setShowDeadlineModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);

  // 마감 기한 지났는지 여부 계산
  useEffect(() => {
    // 초기 계산
    const checkDeadline = () => {
      const timeLeft = calculateTimeLeft(APPLICATION_CLOSE_DATE);
      // calculateTimeLeft의 difference <= 0인 경우(반환값의 모든 필드가 0) isDeadlinePassed는 true
      const deadlinePassed = timeLeft.days === 0 && timeLeft.hours === 0 && 
                            timeLeft.minutes === 0 && timeLeft.seconds === 0;
      setIsDeadlinePassed(deadlinePassed);
    };

    // 초기 계산 실행
    checkDeadline();

    // 1초마다 다시 계산
    const timer = setInterval(checkDeadline, 1000);

    // 컴포넌트 언마운트 시 타이머 정리
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (application && (application.status === 'documentPassed' || application.status === 'documentFailed')) {
      setShowStatusModal(true);
    }
  }, [application]);

  const getStatusLabel = (status) => {
    const statusObj = STATUS_APPLICATIONS.find(s => s.value === status);
    return statusObj ? statusObj.label : '상태 정보 없음';
  };

  const renderStatusMessage = () => {
    if (isDeadlinePassed) {
      return (
        <div className="status-message">
          <p>모집이 마감되었습니다.</p>
          <p>신청서는 수정할 수 없습니다.</p>
        </div>
      );
    }
    
    return null;
  };

  const handleEditClick = () => {
    if (isDeadlinePassed) {
      setShowDeadlineModal(true);
    } else {
      onEdit();
    }
  };

  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
  };

  if (!application) {
    return <div className="loading">Loading your application...</div>;
  }

  return (
    <div className="application-form-outer-container">
      <ApplyTitleHeader title={['K-STARTUP', 'GRAND CHALLENGE 2025']} />
      <div className="application-form-container">
        <div className="application-form-header">
          <div className="application-form-header-left-section">
            <h2>My Application</h2>
            {/* PC에서만 보이는 Edit 버튼 */}
            {!isDeadlinePassed && (
              <button className="edit-button edit-button-desktop" onClick={handleEditClick} style={{ marginLeft: '15px' }}>
                <span className="edit-icon-pc">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </span>
                <span className="edit-text">Edit</span>
              </button>
            )}
          </div>
          <div className="button-group">
            {/* 모바일에서만 보이는 Edit 버튼 */}
            {!isDeadlinePassed && (
              <button className="edit-button edit-button-mobile" onClick={handleEditClick}>
                <span className="edit-text">Edit</span>
                <span className="edit-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </span>
              </button>
            )}
            <ApplyAuth
              isLoggedIn={isLoggedIn}
              userEmail={userEmail}
              onLogout={onLogout}
            />
          </div>
        </div>
        
        {renderStatusMessage()}
        
        <div className="submission-notice">
          <p>Your application has been submitted. You can edit it only before the deadline. After any changes, please make sure to Submit again.</p>
        </div>
        
        <div className="application-details">
          <div className="section">
            <h3>Basic Information</h3>
            <div className="info-row">
              <div className="label">Team Name</div>
              <div className="value">{application?.teamName}</div>
            </div>
            <div className="info-row">
              <div className="label">Entity Status</div>
              <div className="value">{application?.entityStatus}</div>
            </div>
            <div className="info-row">
              <div className="label">Established Date</div>
              <div className="value">{application?.establishedDate}</div>
            </div>
            <div className="info-row">
              <div className="label">Location</div>
              <div className="value">{application?.location}</div>
            </div>
          </div>

          <div className="section">
            <h3>CEO</h3>
            <div className="info-row">
              <div className="label">Name</div>
              <div className="value">{application?.ceo}</div>
            </div>
            <div className="info-row">
              <div className="label">Gender</div>
              <div className="value">{application?.gender}</div>
            </div>
          </div>

          <div className="section">
            <h3>Participant</h3>
            <div className="info-row">
              <div className="label">Name</div>
              <div className="value">{application?.participant}</div>
            </div>
            <div className="info-row">
              <div className="label">Title</div>
              <div className="value">{application?.participantTitle}</div>
            </div>
            <div className="info-row">
              <div className="label">Nationality</div>
              <div className="value">{application?.participantNationality}</div>
            </div>
          </div>

          <div className="section">
            <h3>Business Information</h3>
            <div className="info-row">
              <div className="label">Industry</div>
              <div className="value">{application?.industry}</div>
            </div>
            <div className="info-row-full">
              <div className="label">Business Item</div>
              <div className="value text-area">{application?.businessItem}</div>
            </div>
          </div>

          <div className="section">
            <h3>Online Presence</h3>
            <div className="info-row">
              <div className="label">Website</div>
              <div className="value">
                <a href={application?.website} target="_blank" rel="noopener noreferrer">
                  {application?.website}
                </a>
              </div>
            </div>
            <div className="info-row">
              <div className="label">Social Media</div>
              <div className="value">
                <a href={application?.socialMedia} target="_blank" rel="noopener noreferrer">
                  {application?.socialMedia}
                </a>
              </div>
            </div>
            <div className="info-row">
              <div className="label">Promotional Video</div>
              <div className="value">{application?.promotionalVideo}</div>
            </div>
          </div>

          <div className="section">
            <h3>Application Documents</h3>
            <div className="info-row">
              <div className="label">Application Form</div>
              <div className="value">
                {application?.applicationForm ? (
                  <a href={application?.applicationForm} target="_blank" rel="noopener noreferrer">
                    View Document
                  </a>
                ) : (
                  'Not uploaded'
                )}
              </div>
            </div>
            <div className="info-row">
              <div className="label">IR Pitch Deck</div>
              <div className="value">
                {application?.irPitchDeck ? (
                  <a href={application?.irPitchDeck} target="_blank" rel="noopener noreferrer">
                    View Document
                  </a>
                ) : (
                  'Not uploaded'
                )}
              </div>
            </div>
            <div className="info-row">
              <div className="label">Additional Documents</div>
              <div className="value">
                {application?.additionalDocuments ? (
                  <a href={application?.additionalDocuments} target="_blank" rel="noopener noreferrer">
                    View Document
                  </a>
                ) : (
                  'Not uploaded'
                )}
              </div>
            </div>
          </div>

          <div className="section">
            <h3>Additional Information</h3>
            <div className="info-row">
              <div className="label">Information Channel</div>
              <div className="value">{application?.informationChannel}</div>
            </div>
          </div>
        </div>
      </div>

      {showDeadlineModal && (
        <div className="deadline-modal">
          <div className="deadline-modal-content">
            <p>Cannot modify after the application deadline.</p>
            <button onClick={() => setShowDeadlineModal(false)}>Confirm</button>
          </div>
        </div>
      )}

      <StatusModal 
        isOpen={showStatusModal} 
        onClose={handleCloseStatusModal} 
        status={application?.status} 
      />
    </div>
  );
};

MyApplication.propTypes = {
  application: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  userEmail: PropTypes.string,
  onLogout: PropTypes.func.isRequired
};

export default MyApplication; 